// components/project/economic/FatturazioneV2.jsx
// FATTURAZIONE V2 — Compact control room dei pagamenti cliente.
// SOLO presentazione/read-model (nessuna modifica a lifecycle milestone / Extra payments).
// - Top control panel compatto (NON SBLOCCATO / DA FATTURARE / DA INCASSARE / INCASSATO)
// - Donut "Stato piano pagamenti" (stessi valori canonici del summary)
// - Milestone come mosaico mini-card (click → dettaglio on demand)
// - Extra Forniture raggruppati per extra (UNA card per Extra: Acconto + Saldo), filtri operativi.
import React, { useMemo, useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  Lock, Receipt, CreditCard, CheckCircle, AlertTriangle, RefreshCw, Download,
  Package, Truck, FileText, ChevronDown, ChevronRight, Plus, Euro, Sofa, ExternalLink,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { formatCurrency, formatDate } from '../../../lib/utils';
import { InvoiceModal, PaymentModal, MovementsModal, OverdueBadge } from './BillingWorkflowModals';
import { useNavigate } from 'react-router-dom';

// Deep-link deterministico verso la Control Room globale (identità tranche, no parsing testo).
const buildControlRoomUrl = ({ tab, source_type, source_id, tranche_key, action }) => {
  const q = new URLSearchParams();
  if (tab) q.set('tab', tab);
  q.set('source_type', source_type);
  if (source_id != null) q.set('source_id', String(source_id));
  if (tranche_key != null) q.set('tranche_key', String(tranche_key));
  if (action) q.set('action', action);
  return `/billing-queue?${q.toString()}`;
};
const trancheTarget = (status, base) => {
  if (status === 'da_fatturare') return { ...base, tab: 'da_fatturare', action: 'invoice' };
  if (status === 'fatturato' || status === 'scaduto') return { ...base, tab: 'da_incassare', action: 'payment' };
  if (status === 'incassato') return { ...base, tab: 'incassate', action: 'movements' };
  return base;
};
function ControlRoomLink({ target, label = 'Apri nella Control Room', testid }) {
  const navigate = useNavigate();
  return (
    <button type="button" data-testid={testid}
      onClick={(e) => { e.stopPropagation(); navigate(buildControlRoomUrl(target)); }}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-[#FF7819] hover:underline">
      <ExternalLink className="w-3 h-3" /> {label}
    </button>
  );
}

// Sistema colore: card neutre, colore SOLO sui badge di stato (§29-30).
const STATUS_META = {
  non_sbloccato: { label: 'Non sbloccato', badge: 'bg-gray-100 text-gray-600 border border-gray-200', color: '#9ca3af' },
  da_fatturare: { label: 'Da fatturare', badge: 'bg-amber-100 text-amber-700 border border-amber-200', color: '#f59e0b' },
  fatturato: { label: 'Da incassare', badge: 'bg-blue-100 text-blue-700 border border-blue-200', color: '#3b82f6' },
  incassato: { label: 'Incassato', badge: 'bg-emerald-100 text-emerald-700 border border-emerald-200', color: '#10b981' },
  scaduto: { label: 'Scaduto', badge: 'bg-red-100 text-red-700 border border-red-200', color: '#ef4444' },
};

const sumBy = (arr, pred) => arr.filter(pred).reduce((s, x) => s + (x.amount || 0), 0);

function StatusBadge({ status, testId }) {
  const m = STATUS_META[status] || STATUS_META.da_fatturare;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.badge}`} data-testid={testId}>
      {m.label}
    </span>
  );
}

// ---------- TOP CONTROL PANEL + DONUT ----------
function ControlPanel({ buckets, total, details = {}, reconciliation = null, canAct = false, onAction }) {
  const [openKey, setOpenKey] = useState(null);
  const cells = [
    { key: 'non_sbloccato', label: 'Non sbloccato', value: buckets.non_sbloccato },
    { key: 'da_fatturare', label: 'Da fatturare', value: buckets.da_fatturare },
    { key: 'fatturato', label: 'Da incassare', value: buckets.fatturato },
    { key: 'incassato', label: 'Incassato', value: buckets.incassato },
  ];
  const donutData = cells.filter(c => c.value > 0).map(c => ({ name: STATUS_META[c.key].label, value: c.value, color: STATUS_META[c.key].color }));
  const pctIncassato = total > 0 ? Math.round((buckets.incassato / total) * 100) : 0;
  const visibleItems = (k) => (details[k] || []).filter(t => (t.amount || 0) > 0);
  const openItems = openKey ? visibleItems(openKey) : [];
  const srcIcon = { milestone: FileText, extra_work: Plus, extra_supply: Package, furniture: Sofa };
  const cleanDesc = (t) => {
    const ref = (t.source_ref || t.code || '').trim();
    let d = (t.description || '').trim();
    if (ref && d.startsWith(ref)) d = d.slice(ref.length).replace(/^\s*-\s*/, '').trim();
    return (d && d !== ref) ? d : '';
  };

  return (
    <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm p-4" data-testid="fatt-control-panel">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Strip valori */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
          {cells.map(c => {
            const items = visibleItems(c.key);
            const clickable = items.length > 0;
            const active = openKey === c.key;
            return (
              <button
                type="button"
                key={c.key}
                onClick={() => clickable && setOpenKey(active ? null : c.key)}
                className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${active ? 'border-[#FF7819] bg-[#FFF8F3]' : 'bg-[#FAFAFA] border-[#F0F0F0]'} ${clickable ? 'hover:border-[#FFD1B0] cursor-pointer' : 'cursor-default'}`}
                data-testid={`fatt-summary-${c.key}`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_META[c.key].color }} />
                  <p className="text-xs text-[#737373]">{c.label}</p>
                  <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${clickable ? 'text-[#a3a3a3]' : 'text-transparent'} ${active ? 'rotate-180' : ''}`} />
                </div>
                <p className="text-base xl:text-lg font-bold text-[#171717] leading-tight whitespace-nowrap">{formatCurrency(c.value)}</p>
                <p className="text-[10px] text-[#a3a3a3] mt-0.5">{clickable ? `${items.length} tranche` : '—'}</p>
              </button>
            );
          })}
        </div>
        {/* Donut */}
        <div className="relative shrink-0 mx-auto lg:mx-0" style={{ width: 150, height: 150 }} data-testid="fatt-donut">
          {donutData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={70} paddingAngle={2} stroke="none">
                  {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full rounded-full border-8 border-[#F0F0F0]" />
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="text-2xl font-bold text-[#171717] leading-none">{pctIncassato}%</p>
            <p className="text-[10px] text-[#737373] mt-0.5">incassato</p>
          </div>
        </div>
      </div>

      {/* Dettaglio tranche del bucket selezionato */}
      {openKey && (
        <div className="mt-3 border-t border-[#F0F0F0] pt-3" data-testid={`fatt-detail-${openKey}`}>
          <p className="text-xs font-semibold text-[#737373] uppercase tracking-wide mb-2">
            {STATUS_META[openKey].label} — dettaglio di cosa lo compone
          </p>
          {openItems.length > 0 && (() => {
            const origins = {};
            openItems.forEach(t => { origins[t.source_label] = (origins[t.source_label] || 0) + (t.amount || 0); });
            return (
              <div className="flex flex-wrap gap-2 mb-2" data-testid={`fatt-breakdown-${openKey}`}>
                {Object.entries(origins).map(([label, amt]) => (
                  <span key={label} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0F0F0] text-[#525252]">{label}: <b>{formatCurrency(amt)}</b></span>
                ))}
              </div>
            );
          })()}
          {openItems.length === 0 ? (
            <p className="text-sm text-[#a3a3a3]">Nessuna tranche.</p>
          ) : (
            <div className="space-y-1">
              {openItems.map((t, i) => {
                const SIcon = srcIcon[t.source_type] || FileText;
                const rowAmount = (t.display_amount != null ? t.display_amount : t.amount);
                return (
                  <div key={i} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded bg-[#FAFAFA] flex-wrap" data-testid={`fatt-detail-row-${openKey}`}>
                    <SIcon className="w-4 h-4 text-[#737373] shrink-0" />
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-[#E5E5E5] text-[#525252] shrink-0">{t.source_label}</span>
                    <span className="font-mono text-xs text-[#0078C8] shrink-0">{t.source_ref || t.code}</span>
                    {cleanDesc(t) && <span className="text-[#737373] truncate max-w-[160px]">{cleanDesc(t)}</span>}
                    {t.tranche_type && t.tranche_type !== 'milestone' && (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#6D28D9] shrink-0">{t.tranche_type}</span>
                    )}
                    {openKey === 'fatturato' && t.invoice_number && (
                      <span className="text-[10px] text-[#737373] shrink-0">Fatt. {t.invoice_number}{t.due_date ? ` · scad. ${formatDate(t.due_date)}` : ''}</span>
                    )}
                    {openKey === 'fatturato' && t.is_overdue && <OverdueBadge days={t.days_overdue} />}
                    <span className="ml-auto font-semibold text-[#171717] shrink-0">
                      {formatCurrency(rowAmount)}
                      {openKey === 'fatturato' && t.paid > 0 && (
                        <span className="text-[10px] font-normal text-emerald-600 ml-1">(incassato {formatCurrency(t.paid)})</span>
                      )}
                    </span>
                    {canAct && onAction && (
                      <div className="flex items-center gap-1 w-full justify-end pt-1 sm:w-auto sm:pt-0">
                        {openKey === 'da_fatturare' && (
                          <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => onAction('invoice', t)} data-testid={`action-invoice-${t.source_type}-${t.source_id}-${t.tranche_key}`}>
                            <Receipt className="w-3 h-3 mr-1" /> Registra fattura
                          </Button>
                        )}
                        {openKey === 'fatturato' && (
                          <>
                            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => onAction('payment', t)} data-testid={`action-payment-${t.source_type}-${t.source_id}-${t.tranche_key}`}>
                              <CreditCard className="w-3 h-3 mr-1" /> Registra incasso
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => onAction('movements', t)} data-testid={`action-movements-${t.source_type}-${t.source_id}-${t.tranche_key}`}>
                              Movimenti
                            </Button>
                          </>
                        )}
                        {openKey === 'incassato' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={() => onAction('movements', t)} data-testid={`action-movements-${t.source_type}-${t.source_id}-${t.tranche_key}`}>
                            Movimenti / Storna
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {reconciliation && openKey === 'da_fatturare' && (
            <p className="text-[11px] text-[#0F7A52] mt-2">✓ Include milestone contratto + Extra lavori + Extra forniture + Arredi · nessun doppio conteggio (Δ {formatCurrency(reconciliation.RECONCILIATION_DELTA || 0)})</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- MILESTONE MINI-CARD ----------
function triggerLabel(m) {
  if (m.trigger_type === 'sal_percentuale' && m.trigger_sal_percentage) return `SAL ≥ ${m.trigger_sal_percentage}%`;
  if (m.percentage != null) return `${m.percentage}% contratto`;
  return '';
}

function MilestoneCard({ milestone, blockReason, canAct, onUpdateStatus, onGenerateInvoiceNotice }) {
  const [open, setOpen] = useState(false);
  const m = STATUS_META[milestone.status] || STATUS_META.non_sbloccato;
  return (
    <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm" data-testid={`milestone-${milestone.id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left p-3.5 flex flex-col gap-1.5 hover:bg-[#FAFAFA] transition-colors rounded-xl"
        data-testid={`milestone-toggle-${milestone.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium text-sm text-[#171717] leading-tight">{milestone.name}</p>
          {open ? <ChevronDown className="w-4 h-4 text-[#a3a3a3] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#a3a3a3] shrink-0" />}
        </div>
        <p className="text-lg font-bold text-[#171717] leading-tight">{formatCurrency(milestone.amount)}</p>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className="text-xs text-[#737373]">{triggerLabel(milestone)}</span>
          <StatusBadge status={milestone.status} testId={`milestone-status-${milestone.id}`} />
        </div>
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 pt-0 border-t border-[#F5F5F5] space-y-2" data-testid={`milestone-detail-${milestone.id}`}>
          <div className="pt-2 space-y-1 text-xs">
            {(milestone.invoice_date || milestone.invoiced_at) && <p className="text-blue-600">Fatturato il {formatDate(milestone.invoice_date || milestone.invoiced_at)}</p>}
            {(milestone.payment_date || milestone.paid_at) && <p className="text-emerald-600">Incassato il {formatDate(milestone.payment_date || milestone.paid_at)}</p>}
            {milestone.status === 'non_sbloccato' && (
              <p className="text-[#737373] flex items-center gap-1"><Lock className="w-3 h-3" /> {blockReason || 'Bloccato'}</p>
            )}
          </div>
          {canAct && milestone.status !== 'non_sbloccato' && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              {milestone.status === 'da_fatturare' && (
                <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => onGenerateInvoiceNotice(milestone)} data-testid={`milestone-avviso-${milestone.id}`}>
                  <Download className="w-3 h-3 mr-1" /> Avviso Fattura
                </Button>
              )}
              <ControlRoomLink
                target={trancheTarget(milestone.status, { source_type: 'milestone', source_id: milestone.id, tranche_key: 'milestone' })}
                testid={`milestone-controlroom-${milestone.id}`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- EXTRA FORNITURE GROUPED CARD ----------
function ExtraPaymentDetail({ p, extraId, canAct, markingGoodsReady, markingPaid, onMarkGoodsReady, onDownloadAvviso, onOpenInvoice, onMarkPaid }) {
  const isSaldo = (p.trigger_type || '').includes('saldo');
  return (
    <div className="rounded-lg bg-[#FAFAFA] border border-[#F0F0F0] p-2.5" data-testid={`ef-payment-detail-${p.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-medium text-[#525252]">{isSaldo ? 'Saldo 50%' : 'Acconto 50%'}</span>
        <span className="font-semibold text-sm text-[#171717]">{formatCurrency(p.amount)}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <StatusBadge status={p.status} testId={`ef-payment-status-${p.id}`} />
      </div>
      <div className="mt-1.5 space-y-0.5 text-xs">
        {p.invoice_number && <p className="text-blue-600">Fattura n° {p.invoice_number} del {formatDate(p.invoice_date)}</p>}
        {p.payment_date && <p className="text-emerald-600">Incassato il {formatDate(p.payment_date)}</p>}
        {isSaldo && p.status === 'non_sbloccato' && (
          <p className="text-[#737373] flex items-center gap-1"><Lock className="w-3 h-3" /> In attesa comunicazione merce pronta</p>
        )}
      </div>
      {canAct && (
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          {isSaldo && p.status === 'non_sbloccato' && (
            <Button size="sm" className="h-7 text-xs bg-teal-500 hover:bg-teal-600 text-white"
              onClick={() => onMarkGoodsReady(p.id)} disabled={markingGoodsReady === p.id} data-testid={`mark-goods-ready-${p.id}`}>
              {markingGoodsReady === p.id ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Truck className="w-3 h-3 mr-1" />} Merce Pronta
            </Button>
          )}
          {(p.status === 'da_fatturare' || p.status === 'fatturato') && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
              onClick={(e) => onDownloadAvviso(e, p)} data-testid={`download-avviso-ef-${p.id}`}>
              <FileText className="w-3 h-3 mr-1" /> Avviso
            </Button>
          )}
          {p.status === 'da_fatturare' && (
            <ControlRoomLink
              target={{ tab: 'da_fatturare', source_type: 'extra_supply', source_id: extraId, tranche_key: p.trigger_type || p.id, action: 'invoice' }}
              testid={`ef-controlroom-invoice-${p.id}`} />
          )}
          {p.status === 'fatturato' && (
            <ControlRoomLink
              target={{ tab: 'da_incassare', source_type: 'extra_supply', source_id: extraId, tranche_key: p.trigger_type || p.id, action: 'payment' }}
              label="Registra incasso nella Control Room"
              testid={`ef-controlroom-payment-${p.id}`} />
          )}
        </div>
      )}
    </div>
  );
}

function ExtraCard({ group, canAct, handlers, markingGoodsReady, markingPaid }) {
  const [open, setOpen] = useState(false);
  const { acconto, saldo, total, collected, residual } = group;
  return (
    <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm" data-testid={`extra-card-${group.extra_id}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full text-left p-3.5 hover:bg-[#FAFAFA] transition-colors rounded-xl"
        data-testid={`extra-toggle-${group.extra_id}`}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="text-xs bg-teal-100 text-teal-700 border border-teal-200">{group.extra_number}</Badge>
              <span className="text-xs text-[#737373] truncate">{group.extra_title}</span>
            </div>
          </div>
          {open ? <ChevronDown className="w-4 h-4 text-[#a3a3a3] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#a3a3a3] shrink-0" />}
        </div>
        <p className="text-lg font-bold text-[#171717] leading-tight">{formatCurrency(total)}</p>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {acconto && (
            <span className="inline-flex items-center gap-1 text-xs">
              <span className="text-[#737373]">Acconto</span>
              <StatusBadge status={acconto.status} testId={`extra-acconto-status-${group.extra_id}`} />
            </span>
          )}
          {saldo && (
            <span className="inline-flex items-center gap-1 text-xs">
              <span className="text-[#737373]">Saldo</span>
              <StatusBadge status={saldo.status} testId={`extra-saldo-status-${group.extra_id}`} />
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 mt-2 text-xs">
          <span className="text-emerald-600">Incassato {formatCurrency(collected)}</span>
          <span className="text-[#737373]">Residuo {formatCurrency(residual)}</span>
        </div>
      </button>
      {open && (
        <div className="px-3.5 pb-3.5 pt-2 border-t border-[#F5F5F5] grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`extra-detail-${group.extra_id}`}>
          {acconto && <ExtraPaymentDetail p={acconto} extraId={group.extra_id} canAct={canAct} markingGoodsReady={markingGoodsReady} markingPaid={markingPaid} {...handlers} />}
          {saldo && <ExtraPaymentDetail p={saldo} extraId={group.extra_id} canAct={canAct} markingGoodsReady={markingGoodsReady} markingPaid={markingPaid} {...handlers} />}
        </div>
      )}
    </div>
  );
}

// ---------- MAIN ----------
export default function FatturazioneV2({
  projectId = null, onWorkflowChange = null,
  milestones = [], milestonesLoading = false, unlockStatus = null,
  extraForniturePayments = [], billingSummary = null, isAdmin = false, isAdministration = false,
  efMarkingGoodsReady = null, efMarkingPaid = null,
  onRegenerateMilestones, onUpdateMilestoneStatus, onGenerateInvoiceNotice,
  onEfMarkGoodsReady, onDownloadEfAvviso, onOpenEfInvoiceDialog, onEfMarkPaid,
}) {
  const canAct = isAdmin || isAdministration;

  // FASE BILLING 1.4 — modali workflow (fattura / incasso / storno)
  const [wfModal, setWfModal] = useState({ type: null, tranche: null });
  const handleWorkflowAction = (type, tranche) => setWfModal({ type, tranche });
  const closeWfModal = () => setWfModal({ type: null, tranche: null });

  // Buckets UNIFICATI dal backend (milestone + extra lavori + extra forniture).
  // Fallback alle sole milestone se il summary non è ancora disponibile.
  const buckets = useMemo(() => {
    if (billingSummary?.buckets) {
      return {
        non_sbloccato: billingSummary.buckets.non_sbloccato || 0,
        da_fatturare: billingSummary.buckets.da_fatturare || 0,
        fatturato: billingSummary.buckets.da_incassare || 0,
        incassato: billingSummary.buckets.incassato || 0,
      };
    }
    return {
      non_sbloccato: sumBy(milestones, m => m.status === 'non_sbloccato'),
      da_fatturare: sumBy(milestones, m => m.status === 'da_fatturare'),
      fatturato: sumBy(milestones, m => m.status === 'fatturato'),
      incassato: sumBy(milestones, m => m.status === 'incassato'),
    };
  }, [billingSummary, milestones]);
  const planTotal = buckets.non_sbloccato + buckets.da_fatturare + buckets.fatturato + buckets.incassato;
  const bucketDetails = {
    non_sbloccato: billingSummary?.details?.non_sbloccato || [],
    da_fatturare: billingSummary?.details?.da_fatturare || [],
    fatturato: billingSummary?.details?.da_incassare || [],
    incassato: billingSummary?.details?.incassato || [],
  };

  const blockReasonFor = (mid) => unlockStatus?.milestones?.find(x => x.milestone_id === mid)?.block_reason;

  // Read-model raggruppato Extra: UNA card per extra_id (Acconto + Saldo)
  const extraGroups = useMemo(() => {
    const map = new Map();
    for (const p of extraForniturePayments) {
      const key = p.extra_id || p.extra_number;
      if (!map.has(key)) {
        map.set(key, { extra_id: key, extra_number: p.extra_number, extra_title: p.extra_title || p.name, payments: [] });
      }
      map.get(key).payments.push(p);
    }
    const groups = Array.from(map.values()).map(g => {
      const acconto = g.payments.find(p => (p.trigger_type || '').includes('acconto'));
      const saldo = g.payments.find(p => (p.trigger_type || '').includes('saldo'));
      const total = g.payments.reduce((s, p) => s + (p.amount || 0), 0);
      const collected = g.payments.filter(p => p.status === 'incassato').reduce((s, p) => s + (p.amount || 0), 0);
      return { ...g, acconto, saldo, total, collected, residual: Math.max(total - collected, 0) };
    });
    groups.sort((a, b) => (a.extra_number || '').localeCompare(b.extra_number || ''));
    return groups;
  }, [extraForniturePayments]);

  // Filtri operativi Extra
  const hasStatus = (g, s) => g.payments.some(p => p.status === s);
  const filterPredicates = {
    da_gestire: (g) => hasStatus(g, 'da_fatturare') || hasStatus(g, 'fatturato'),
    da_fatturare: (g) => hasStatus(g, 'da_fatturare'),
    da_incassare: (g) => hasStatus(g, 'fatturato'),
    in_attesa: (g) => hasStatus(g, 'non_sbloccato') && !hasStatus(g, 'da_fatturare') && !hasStatus(g, 'fatturato'),
    tutti: () => true,
  };
  const counts = {
    da_gestire: extraGroups.filter(filterPredicates.da_gestire).length,
    da_fatturare: extraGroups.filter(filterPredicates.da_fatturare).length,
    da_incassare: extraGroups.filter(filterPredicates.da_incassare).length,
    in_attesa: extraGroups.filter(filterPredicates.in_attesa).length,
    tutti: extraGroups.length,
  };
  const defaultFilter = counts.da_gestire > 0 ? 'da_gestire' : 'tutti';
  const [filter, setFilter] = useState(defaultFilter);
  const [filterTouched, setFilterTouched] = useState(false);
  // Se i dati Extra arrivano dopo il mount, allinea il default una sola volta (finché l'utente non sceglie).
  useEffect(() => {
    if (!filterTouched) setFilter(defaultFilter);
  }, [defaultFilter, filterTouched]);
  const visibleGroups = extraGroups.filter(filterPredicates[filter] || filterPredicates.tutti);

  const extraHandlers = {
    onMarkGoodsReady: onEfMarkGoodsReady,
    onDownloadAvviso: onDownloadEfAvviso,
    onOpenInvoice: onOpenEfInvoiceDialog,
    onMarkPaid: onEfMarkPaid,
  };

  const FILTER_TABS = [
    { key: 'da_gestire', label: 'Da gestire' },
    { key: 'da_fatturare', label: 'Da fatturare' },
    { key: 'da_incassare', label: 'Da incassare' },
    { key: 'in_attesa', label: 'In attesa' },
    { key: 'tutti', label: 'Tutti' },
  ];

  return (
    <div className="space-y-5" data-testid="fatturazione-v2">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-h4">Fatturazione e Incassi</h3>
          <p className="text-sm text-[#737373]">Control room dei pagamenti cliente</p>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={onRegenerateMilestones} data-testid="regenerate-milestones-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Rigenera Milestone
          </Button>
        )}
      </div>

      {milestonesLoading ? (
        <div className="text-center py-8"><p className="text-[#737373]">Caricamento...</p></div>
      ) : milestones.length === 0 && extraGroups.length === 0 ? (
        <Card className="card-abitalo">
          <CardContent className="p-8 text-center">
            <Euro className="w-12 h-12 text-[#DCDCDC] mx-auto mb-3" />
            <p className="text-[#737373] mb-4">Nessuna milestone di pagamento generata</p>
            {isAdmin && (
              <Button onClick={onRegenerateMilestones}><Plus className="w-4 h-4 mr-2" /> Genera Milestone</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* PART C — Control panel + donut */}
          {(milestones.length > 0 || planTotal > 0) && <ControlPanel buckets={buckets} total={planTotal} details={bucketDetails} reconciliation={billingSummary?.reconciliation} canAct={canAct} onAction={handleWorkflowAction} />}

          {/* PART D — Milestone mosaic */}
          {milestones.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-[#525252] mb-2">Milestone contrattuali</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-start" data-testid="milestone-grid">
                {[...milestones].sort((a, b) => (a.order || 0) - (b.order || 0)).map(m => (
                  <MilestoneCard key={m.id} milestone={m} blockReason={blockReasonFor(m.id)} canAct={canAct}
                    onUpdateStatus={onUpdateMilestoneStatus} onGenerateInvoiceNotice={onGenerateInvoiceNotice} />
                ))}
              </div>
            </div>
          )}

          {/* PART E/F — Extra Forniture raggruppati + filtri */}
          {extraGroups.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Package className="w-4 h-4 text-teal-600" />
                <h4 className="text-sm font-semibold text-[#525252]">Extra Forniture</h4>
                <Badge className="bg-teal-100 text-teal-700 border border-teal-200">{extraGroups.length}</Badge>
              </div>
              {/* Filter bar */}
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="extra-filter-bar">
                {FILTER_TABS.map(t => (
                  <button key={t.key} type="button" onClick={() => { setFilter(t.key); setFilterTouched(true); }}
                    className={`px-3 py-1.5 text-xs rounded-full transition-colors border ${filter === t.key ? 'bg-[#FF7819] text-white border-[#FF7819]' : 'bg-white text-[#525252] border-[#EDEDED] hover:bg-[#F5F5F5]'}`}
                    data-testid={`extra-filter-${t.key}`}>
                    {t.label} <span className={`ml-1 ${filter === t.key ? 'text-white/80' : 'text-[#a3a3a3]'}`}>{counts[t.key]}</span>
                  </button>
                ))}
              </div>
              {visibleGroups.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#EDEDED] bg-[#FAFAFA] p-6 text-center text-sm text-[#a3a3a3]" data-testid="extra-empty-filter">
                  Nessun Extra in questo stato
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start" data-testid="extra-grid">
                  {visibleGroups.map(g => (
                    <ExtraCard key={g.extra_id} group={g} canAct={canAct} handlers={extraHandlers}
                      markingGoodsReady={efMarkingGoodsReady} markingPaid={efMarkingPaid} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* FASE BILLING 1.4 — modali workflow economico */}
      <InvoiceModal open={wfModal.type === 'invoice'} onClose={closeWfModal} projectId={projectId}
        tranche={wfModal.type === 'invoice' ? wfModal.tranche : null} onDone={onWorkflowChange} />
      <PaymentModal open={wfModal.type === 'payment'} onClose={closeWfModal} projectId={projectId}
        tranche={wfModal.type === 'payment' ? wfModal.tranche : null} onDone={onWorkflowChange} />
      <MovementsModal open={wfModal.type === 'movements'} onClose={closeWfModal} projectId={projectId}
        tranche={wfModal.type === 'movements' ? wfModal.tranche : null} onDone={onWorkflowChange} />
    </div>
  );
}
